/**
 * GST Invoices · Feb 2026
 * Sales & Dispatch · Generate a Tax Invoice against a Sales Order,
 * Delivery Challan or Packing List. Handles Draft → Issued → Amend /
 * Cancel workflow, auto CGST+SGST vs IGST split (based on the seller's
 * and buyer's GSTIN state codes) and prints an A4 tax-invoice PDF.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileTextIcon, PlusIcon, PrinterIcon, PencilLineIcon, TrashIcon,
  XCircleIcon, CheckCircleIcon, WarningIcon, ProhibitIcon, EyeIcon, XIcon,
} from "@phosphor-icons/react";
import { getUser } from "@/lib/secureStorage";
import { canSubmitDoc } from "@/lib/submitPerms";
import { useAuth } from "@/lib/auth";
import ApprovalControls, { ApprovalBadge } from "@/components/ApprovalControls";
import DocumentInfo from "@/components/DocumentInfo";
import { DocDownloadButtons, DocDownloadIcons, useDocStyles } from "@/components/DocDownloadActions";
import { RowActionsMenu } from "@/components/RowActionsMenu";

// Feb 2026 · Every amount on a GST invoice is INR (2 dp, Indian commas).
// Route through the shared `lib/uom` helper so the on-screen numbers
// match the ReportLab PDF letter-for-letter.
import { fmtInr as _fmtInr } from "@/lib/uom";
const fmt = (v) => _fmtInr(v, { symbol: false });
const STATUS_STYLE = {
  Draft:     "border-zinc-500/40 text-zinc-300",
  Issued:    "border-emerald-500/40 text-emerald-300 bg-emerald-500/5",
  Amended:   "border-amber-500/40 text-amber-300 bg-amber-500/5",
  Cancelled: "border-red-500/40 text-red-300 bg-red-500/5",
};

// DP-GST-AUTH-CONTROL · Phase 2 · Feb 2026 — canonical Authorised /
// Pending / Rejected predicates. Mirrors the sealed PI helper in
// `pages/ProformaInvoices.jsx` (`isAuthorisedPi`). Rejected is NOT
// Authorised — a Rejected GST remains correctable and resubmittable.
const AUTHORISED_APPROVAL_STATUSES = new Set(["Approved", "Authorized", "Authorised"]);
const isAuthorisedGst = (inv) =>
  AUTHORISED_APPROVAL_STATUSES.has((inv?.approval_status || "").trim());
const isPendingGst = (inv) =>
  (inv?.approval_status || "").trim() === "Pending Approval";
const isRejectedGst = (inv) =>
  (inv?.approval_status || "").trim() === "Rejected";

export default function GstInvoices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // full invoice doc
  const user = getUser();
  const { hasPerm } = useAuth();
  // Aug 31, 2026 · Zero-Trust — DB-backed. Any admin-created role
  // holding `gst_invoices:create` or `gst_invoices:edit` sees the
  // New / Edit / Delete controls. Administrator always passes.
  const canEdit = user?.role === "Administrator"
    || hasPerm?.("gst_invoices:create") || hasPerm?.("gst_invoices:edit");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gst-invoices/");
      setRows(data || []);
    } catch (e) {
      toast.error("Failed to load GST invoices");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter && r.status !== statusFilter) return false;
      if (!needle) return true;
      const hay = [
        r.invoice_no, r.buyer?.name, r.buyer?.gstin, (r.source || {}).no,
        r.customer_po_no,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q, statusFilter]);

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = async (inv) => {
    try {
      const { data } = await api.get(`/gst-invoices/${inv.id}`);
      setEditing(data);
      setEditorOpen(true);
    } catch (e) { toast.error("Failed to open invoice"); }
  };

  const printPdf = async (inv) => {
    try {
      const res = await api.get(`/gst-invoices/${inv.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) { toast.error("Failed to open PDF"); }
  };

  // Feb 25, 2026 · A4 @media print HTML view — opens the invoice in a
  // new browser tab with a "Print / Save PDF" button. Uses the same
  // backend recompute so figures match the ReportLab PDF exactly.
  // Feb 25, 2026 · Blob-URL approach — document.write can leave Safari
  // in a mid-parse state when the user hits Save-as-PDF, producing a
  // blank output. A real object URL is a proper document from Safari's
  // POV and Save-as-PDF captures its rendered layout reliably.
  const openHtmlInNewTab = (html) => {
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url  = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) { toast.error("Popup blocked — allow popups for this site"); URL.revokeObjectURL(url); return; }
    // Free the blob after the tab has had time to load. 60 s is safe.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const printHtml = async (inv) => {
    try {
      const res = await api.get(`/gst-invoices/${inv.id}/print.html`, { responseType: "text" });
      openHtmlInNewTab(res.data);
    } catch (e) { toast.error("Failed to open browser print view"); }
  };

  // Feb 25, 2026 · e-Way Bill · dispatch slip flow.
  const [ewbInv, setEwbInv] = useState(null);
  const [ewbForm, setEwbForm] = useState({
    transporter_name: "", transporter_gstin: "", vehicle_no: "",
    transport_mode: "Road", distance_km: "", from_pin: "", to_pin: "",
    reason: "Supply", sub_type: "Supply", force: false,
  });
  const [ewbSaving, setEwbSaving] = useState(false);
  const openEwbDialog = (inv) => {
    setEwbInv(inv);
    const existing = inv.e_way_bill || {};
    setEwbForm({
      transporter_name: existing.transporter_name || "",
      transporter_gstin: existing.transporter_gstin || "",
      vehicle_no: existing.vehicle_no || "",
      transport_mode: existing.transport_mode || "Road",
      distance_km: existing.distance_km || "",
      from_pin: existing.from_pin || "",
      to_pin: existing.to_pin || "",
      reason: existing.reason || "Supply",
      sub_type: existing.sub_type || "Supply",
      force: false,
    });
  };
  const submitEwb = async () => {
    if (!ewbInv) return;
    if (!ewbForm.vehicle_no.trim()) { toast.error("Vehicle number is required"); return; }
    setEwbSaving(true);
    try {
      const { data } = await api.post(`/gst-invoices/${ewbInv.id}/e-way-bill`, ewbForm);
      toast.success(`e-Way Bill generated · EBN ${data.e_way_bill.ebn}`);
      setEwbInv(null);
      await load();
      // Open the printable slip immediately via Blob URL (Safari-safe).
      const res = await api.get(`/gst-invoices/${ewbInv.id}/e-way-bill.html`, { responseType: "text" });
      openHtmlInNewTab(res.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "e-Way Bill generation failed";
      if (String(msg).includes("₹50,000 e-Way Bill threshold")) {
        if (window.confirm(`${msg}\n\nForce-generate anyway?`)) {
          setEwbForm(prev => ({ ...prev, force: true }));
        }
      } else {
        toast.error(msg);
      }
    } finally { setEwbSaving(false); }
  };
  const printEwbHtml = async (inv) => {
    try {
      const res = await api.get(`/gst-invoices/${inv.id}/e-way-bill.html`, { responseType: "text" });
      openHtmlInNewTab(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Open e-Way Bill first");
    }
  };

  const cancel = async (inv) => {
    const reason = window.prompt(`Cancel ${inv.invoice_no}?\n\nEnter reason (min 4 chars, kept in audit log):`);
    if (!reason || reason.trim().length < 4) { if (reason !== null) toast.error("Reason must be ≥ 4 chars"); return; }
    try {
      await api.post(`/gst-invoices/${inv.id}/cancel`, { reason: reason.trim() });
      toast.success(`${inv.invoice_no} · cancelled`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };

  const removeDraft = async (inv) => {
    // Feb 2026 · Now handles both Draft (silent delete) and non-Draft
    // (Admin-only, strong confirm since it breaks the FY sequence).
    const isDraft = inv.status === "Draft";
    const label = inv.invoice_no || `draft ${inv.id.slice(0, 8)}`;
    const warn = isDraft
      ? `Delete draft ${label}? This cannot be undone.`
      : `⚠ DELETE ${inv.status} invoice ${label}?\n\nThis is a numbered tax invoice — deleting it breaks the FY sequence.\nPrefer Cancel unless you're sure.\n\nType YES to confirm:`;
    if (isDraft) {
      if (!window.confirm(warn)) return;
    } else {
      const resp = window.prompt(warn, "");
      if (!resp || resp.trim().toUpperCase() !== "YES") return;
    }
    try {
      await api.delete(`/gst-invoices/${inv.id}`);
      toast.success(`${label} · deleted`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // DP-GST-AUTH-CONTROL · Phase 2 · Clause A · Feb 2026 — explicit
  // Submit / Resubmit for Management Authorisation. Save ≠ Submit.
  const submit = async (inv) => {
    const label = inv.invoice_no || `draft ${inv.id.slice(0, 8)}`;
    const isResubmit = isRejectedGst(inv);
    const prompt = isResubmit
      ? `Resubmit ${label} for Management Authorisation?\n\nThe invoice will re-enter the Management Authorisation Queue.`
      : `Submit ${label} for Management Authorisation?\n\nOnce submitted, direct edits are locked until the request is Authorised or Rejected.`;
    if (!window.confirm(prompt)) return;
    try {
      await api.post(`/gst-invoices/${inv.id}/submit-for-approval`);
      toast.success(isResubmit
        ? `${label} · re-submitted for authorisation`
        : `${label} · submitted for authorisation`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="gst-invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-3">
            <FileTextIcon size={26} weight="bold" className="text-[var(--accent)]" />
            GST Invoices
          </h1>
          <p className="text-sm text-zinc-400 mt-1">Tax invoices against Sales Orders, Delivery Challans and Packing Lists.</p>
        </div>
        {canEdit && (
          <button
            data-testid="gst-new-invoice"
            onClick={openNew}
            className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold flex items-center gap-2"
          >
            <PlusIcon size={14} weight="bold" /> New Invoice
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <input
          data-testid="gst-search"
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search by invoice no / buyer / GSTIN / source doc…"
          className="flex-1 h-10 px-3 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-[var(--accent)]"
        />
        <select
          data-testid="gst-status-filter"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="h-10 px-3 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm text-sm"
        >
          <option value="">All statuses</option>
          <option>Draft</option><option>Issued</option><option>Amended</option><option>Cancelled</option>
        </select>
      </div>

      {/* Grid */}
      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="px-3 py-2 text-left">Invoice No</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Buyer</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-left">Tax</th>
              <th className="px-3 py-2 text-right">Taxable</th>
              <th className="px-3 py-2 text-right">Grand Total</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Authorisation</th>
              <th className="px-3 py-2 text-right w-52">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="gst-rows">
            {loading ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-zinc-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-zinc-500" data-testid="gst-empty">No invoices — click New Invoice to create one.</td></tr>
            ) : filtered.map(r => (
              <tr key={r.id} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/30" data-testid={`gst-row-${r.invoice_no || r.id.slice(0, 8)}`}>
                <td className="px-3 py-2 font-mono text-[var(--accent)]">{r.invoice_no || <span className="text-zinc-500">— draft —</span>}</td>
                <td className="px-3 py-2 text-zinc-300">{r.invoice_date || "—"}</td>
                <td className="px-3 py-2">
                  <div className="font-mono">{r.buyer?.name || "—"}</div>
                  <div className="text-[10px] text-zinc-500">{r.buyer?.gstin || ""}</div>
                </td>
                <td className="px-3 py-2 text-zinc-300 text-xs">{(r.source || {}).type ? `${r.source.type} · ${r.source.no || ""}` : "—"}</td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-zinc-500/40 text-[10px] font-mono uppercase tracking-widest">
                    {(r.tax_type || "").toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums">{fmt(r.totals?.taxable_value)}</td>
                <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold">₹ {fmt(r.totals?.grand_total)}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLE[r.status] || ""}`}>
                    {r.status}
                  </span>
                </td>
                {/* Sep 04, 2026 · Authorisation badge column — reflects the
                    live `approval_status` (Draft / Authorized / Rejected) so
                    the list view stays in sync the instant a manager clicks
                    Authorise inside the editor. */}
                <td className="px-3 py-2">
                  <ApprovalBadge
                    status={r.approval_status}
                    reason={r.rejection_reason}
                    testId={`gst-approval-badge-${r.id.slice(0, 8)}`}
                  />
                </td>
                <td className="px-3 py-2 text-right">
                  <RowActionsMenu testId={`gst-actions-${r.id.slice(0, 8)}`}>
                  {/* View · always available · opens the editor which
                      auto-switches to read-only for Authorised /
                      Pending / Cancelled / EWB-locked records. */}
                  <button title="View" data-testid={`gst-view-${r.id.slice(0, 8)}`} onClick={() => openEdit(r)} className="p-1.5 rounded-sm text-sky-300 hover:bg-sky-500/10 border border-sky-500/30"><EyeIcon size={13} /></button>
                  <button title="Open / Edit" data-testid={`gst-edit-${r.id.slice(0, 8)}`} onClick={() => openEdit(r)} className="p-1.5 rounded-sm text-zinc-300 hover:bg-[var(--bg-2)]"><PencilLineIcon size={13} /></button>
                  {r.invoice_no && (
                    <>
                      {/* Mar 2026 · Print/PDF removed — Browser Print (A4 HTML) is the single canonical format. */}
                      <button title="Browser Print (A4 HTML)" data-testid={`gst-print-html-${r.id.slice(0, 8)}`} onClick={() => printHtml(r)} className="p-1.5 rounded-sm text-sky-300 hover:bg-sky-500/10 border border-sky-500/30"><PrinterIcon size={13} weight="bold" /></button>
                      <DocDownloadIcons type="GST" id={r.id} testPrefix={`gst-row-${r.id.slice(0, 8)}`} />
                      {r.e_way_bill?.ebn ? (
                        <button title={`Print e-Way Bill · EBN ${r.e_way_bill.ebn}`} data-testid={`gst-ewb-print-${r.id.slice(0, 8)}`} onClick={() => printEwbHtml(r)} className="px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/10">EWB ✓</button>
                      ) : (
                        r.status !== "Cancelled" && r.status !== "Draft" && (
                          <button title="Generate e-Way Bill" data-testid={`gst-ewb-new-${r.id.slice(0, 8)}`} onClick={() => openEwbDialog(r)} className="px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest text-amber-300 border border-amber-500/40 hover:bg-amber-500/10">e-Way</button>
                        )
                      )}
                    </>
                  )}
                  {/* DP-GST-AUTH-CONTROL · Phase 2 · Clause A / G — Submit /
                      Resubmit for Draft rows in "Not Submitted" or "Rejected"
                      approval state. Not available once Pending or Authorised
                      (queue takes over) or after Issue. */}
                  {canEdit && canSubmitDoc("GST") && r.status === "Draft" && !isAuthorisedGst(r) && !isPendingGst(r) && (
                    <button
                      title={isRejectedGst(r) ? "Resubmit for Management Authorisation" : "Submit for Management Authorisation"}
                      data-testid={`gst-${isRejectedGst(r) ? "resubmit" : "submit"}-${r.id.slice(0, 8)}`}
                      onClick={() => submit(r)}
                      className={`px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest border ${
                        isRejectedGst(r)
                          ? "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                          : "border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10"
                      }`}>
                      {isRejectedGst(r) ? "Resubmit" : "Submit"}
                    </button>
                  )}
                  {/* DP-GST-AUTH-CONTROL · Phase 2 · Clause D — direct Delete
                      is prohibited on Authorised GST. Hide the button too
                      (backend enforcement remains authoritative). */}
                  {canEdit && r.status === "Draft" && !isAuthorisedGst(r) && (
                    <button title="Delete draft" data-testid={`gst-delete-${r.id.slice(0, 8)}`} onClick={() => removeDraft(r)} className="p-1.5 rounded-sm text-red-300 hover:bg-red-500/10"><TrashIcon size={13} /></button>
                  )}
                  {canEdit && r.status !== "Draft" && r.status !== "Cancelled" && (
                    <button title="Cancel invoice (with reason · audit-preserving)" data-testid={`gst-cancel-${r.id.slice(0, 8)}`} onClick={() => cancel(r)} className="p-1.5 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"><ProhibitIcon size={13} weight="bold" /></button>
                  )}
                  {/* Feb 2026 · Admin-only hard delete for non-Draft invoices.
                      Draft rows are handled above; this button covers Issued /
                      Amended / Cancelled and prompts a strong "type YES" confirm.
                      DP-GST-AUTH-CONTROL · Phase 2 · Clause D — also hidden on
                      Authorised records (backend blocks regardless). */}
                  {user?.role === "Administrator" && r.status !== "Draft" && !isAuthorisedGst(r) && (
                    <button title="Delete invoice (Admin)" data-testid={`gst-hard-delete-${r.id.slice(0, 8)}`} onClick={() => removeDraft(r)} className="p-1.5 rounded-sm text-red-400 hover:bg-red-500/10 border border-red-500/20"><TrashIcon size={13} /></button>
                  )}
                  </RowActionsMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editorOpen && (
        <InvoiceEditor
          initial={editing}
          onClose={() => { setEditorOpen(false); setEditing(null); }}
          onSaved={async () => { setEditorOpen(false); setEditing(null); await load(); }}
        />
      )}
      {/* Feb 25, 2026 · e-Way Bill generation dialog */}
      <Dialog open={!!ewbInv} onOpenChange={(o) => !o && setEwbInv(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl" data-testid="ewb-dialog" description="GST Invoice action — draft, preview or cancel a domestic tax invoice.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 uppercase tracking-widest">
              Generate e-Way Bill · {ewbInv?.invoice_no}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2 text-sm">
            <label className="col-span-2 text-xs text-zinc-400">
              Grand Total: <span className="font-mono text-white">₹ {Number(ewbInv?.totals?.grand_total || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
              {ewbInv?.totals?.grand_total < 50000 && <span className="ml-2 text-amber-400">(below ₹50k threshold — force required)</span>}
            </label>
            <div><div className="overline mb-1">Transporter Name</div>
              <input className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" data-testid="ewb-transporter-name"
                     value={ewbForm.transporter_name} onChange={e => setEwbForm(p => ({ ...p, transporter_name: e.target.value }))} placeholder="M/s XYZ Logistics" /></div>
            <div><div className="overline mb-1">Transporter GSTIN</div>
              <input className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" data-testid="ewb-transporter-gstin"
                     value={ewbForm.transporter_gstin} onChange={e => setEwbForm(p => ({ ...p, transporter_gstin: e.target.value.toUpperCase() }))} maxLength={15} placeholder="27AAAAA0000A1Z5" /></div>
            <div><div className="overline mb-1">Vehicle No <span className="text-red-400">*</span></div>
              <input className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono uppercase" data-testid="ewb-vehicle-no"
                     value={ewbForm.vehicle_no} onChange={e => setEwbForm(p => ({ ...p, vehicle_no: e.target.value.toUpperCase() }))} placeholder="MH04AB1234" /></div>
            <div><div className="overline mb-1">Transport Mode</div>
              <select className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" data-testid="ewb-mode"
                      value={ewbForm.transport_mode} onChange={e => setEwbForm(p => ({ ...p, transport_mode: e.target.value }))}>
                <option>Road</option><option>Rail</option><option>Air</option><option>Ship</option>
              </select></div>
            <div><div className="overline mb-1">Distance (km)</div>
              <input type="number" className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" data-testid="ewb-distance"
                     value={ewbForm.distance_km} onChange={e => setEwbForm(p => ({ ...p, distance_km: e.target.value }))} placeholder="450" /></div>
            <div><div className="overline mb-1">Reason / Sub-Type</div>
              <select className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" data-testid="ewb-reason"
                      value={ewbForm.reason} onChange={e => setEwbForm(p => ({ ...p, reason: e.target.value }))}>
                <option>Supply</option><option>Export</option><option>Job Work</option><option>SKD/CKD</option><option>Recipient not known</option><option>For own use</option><option>Exhibition or fairs</option><option>Line Sales</option><option>Others</option>
              </select></div>
            <div><div className="overline mb-1">From PIN</div>
              <input className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" data-testid="ewb-from-pin"
                     value={ewbForm.from_pin} onChange={e => setEwbForm(p => ({ ...p, from_pin: e.target.value.replace(/\D/g, "").slice(0, 6) }))} placeholder="400001" /></div>
            <div><div className="overline mb-1">To PIN</div>
              <input className="w-full h-9 px-2.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" data-testid="ewb-to-pin"
                     value={ewbForm.to_pin} onChange={e => setEwbForm(p => ({ ...p, to_pin: e.target.value.replace(/\D/g, "").slice(0, 6) }))} placeholder="380001" /></div>
            <label className="col-span-2 flex items-center gap-2 text-xs text-amber-300">
              <input type="checkbox" data-testid="ewb-force" checked={!!ewbForm.force} onChange={e => setEwbForm(p => ({ ...p, force: e.target.checked }))} />
              Force-generate even if grand total is below ₹50,000 (intra-state / mandatory-state override)
            </label>
          </div>
          <DialogFooter>
            <button onClick={() => setEwbInv(null)} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">Cancel</button>
            <button data-testid="ewb-submit" onClick={submitEwb} disabled={ewbSaving}
                    className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-sm">
              {ewbSaving ? "Generating…" : "Generate & Print"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -----------------------------------------------------------------
//  Editor Dialog
// -----------------------------------------------------------------
function InvoiceEditor({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const savedStyles = useDocStyles(initial?.id ? "GST" : null, initial?.id);
  // Feb 28, 2026 · Edit window is OPEN until either (a) the invoice is
  // Cancelled, or (b) an e-Way Bill has been generated (EBN allocated).
  // Between Issue and EWB, ops can still amend line items / addresses /
  // vehicle info — mirrors real-world tax practice (invoice locks with
  // the eway bill, not with issue).
  //
  // DP-GST-AUTH-CONTROL · Phase 2 · Clause D / H · Feb 2026 — extended
  // readOnly to fold in Authorised (approval_status ∈ Authorised set)
  // AND Pending Authorisation states so direct commercial edits are
  // suppressed at the UI layer too. Backend enforcement remains
  // authoritative (see routes/gst_invoices.py::update_invoice).
  const hasEwb = !!initial?.e_way_bill?.ebn;
  const lockedForApproval = !!initial
    && (isAuthorisedGst(initial) || isPendingGst(initial));
  const readOnly = initial
    && (initial.status === "Cancelled" || hasEwb || lockedForApproval);
  const isAmendable = initial && ["Issued", "Amended"].includes(initial.status) && !hasEwb && !isAuthorisedGst(initial);
  const [srcType, setSrcType] = useState(initial?.source?.type === "CUST" ? "CUST" : "SO");
  const [srcOptions, setSrcOptions] = useState([]);
  const [srcId, setSrcId] = useState(initial?.source?.id || "");
  const [busy, setBusy] = useState(false);
  // Aug 2026 · SO → DC picker state. When the user picks an SO, we hit
  // /so-dc-picker/{so_id} to load its DCs (with per-line shipped +
  // already-invoiced + invoicable balance), then let the user tick the
  // DCs being billed. Quantities are locked to the roll-up on Prefill.
  const [soDcPicker, setSoDcPicker] = useState(null);
  const [pickedDcIds, setPickedDcIds] = useState([]);
  const [inv, setInv] = useState(initial || {
    invoice_date: new Date().toISOString().slice(0, 10),
    source: {}, buyer: {}, seller: {}, lines: [], tax_type: "intra",
    charges: { freight: 0, packing: 0, tcs_pct: 0, round_off: 0, tcs_amt: 0 },
    totals: {}, notes: "",
    terms: "Payment due within agreed credit period. Interest @ 18% p.a. on overdue amounts.",
    customer_po_no: "", vehicle_no: "", lr_no: "", status: "Draft",
    invoice_no: "",  // manual-entry mode writes into this on Draft
    // Feb 2026 · four separately-visible references (DC · PL · SO · Cust PO)
    // — auto-filled on DC / PL / SO prefill and editable per invoice.
    references: { so_no: "", dc_no: "", pl_nos: [], customer_po_no: "" },
  });

  // Aug 2026 · Fetch Domestic-GST numbering config so the editor knows
  // whether to expose a mandatory `Invoice Number` field (manual mode)
  // or leave the field blank until Issue (auto mode). Falls back to
  // auto-generation on network error so nothing breaks.
  const [numCfg, setNumCfg] = useState({ auto_generation: true, prefix: "", starting_sequence: 1 });
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/invoice-numbering");
        setNumCfg(data?.gst || { auto_generation: true, prefix: "", starting_sequence: 1 });
      } catch { /* keep default = auto ON */ }
    })();
  }, []);

  // Feb 2026 · Preload masters so the editor can offer:
  //   · a Customer picker when the user chooses "Direct Buyer (no SO)"
  //   · an Article-Master datalist on every line row (still free-text so
  //     one-off items work without a master entry).
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  // Feb 2026 · T&C templates — loaded once so the editor can offer a
  // picker + free-form textarea for the final resolved text.
  const [tncTemplates, setTncTemplates] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [cRes, aRes, tRes] = await Promise.all([
          api.get("/customers/"),
          api.get("/articles/?limit=2000").catch(() => api.get("/articles/")),
          api.get("/tnc-templates/?template_type=sales"),
        ]);
        setCustomers(cRes.data || []);
        setArticles((aRes.data || []).filter(a => a.is_active !== false).map(a => ({
          id: a.id, article_no: a.product_code || a.code || "",
          description: a.description || "",
          hsn_code: a.hs_code || a.hsn_code || "",
          rate: a.rate || 0, unit: a.unit || "MTR",
          gst_rate: a.gst_rate || 5,
        })));
        const tpls = tRes.data || [];
        setTncTemplates(tpls);
        // Feb 2026 · On a fresh draft, mirror the backend's auto-apply so
        // the editor already shows the default T&C text (and stamps the
        // template id for traceability). Never overwrite existing text.
        if (isNew) {
          const def = tpls.find(t => t.is_default);
          if (def) {
            setInv(prev => (prev.terms_and_conditions || prev.tnc_template_id)
              ? prev
              : { ...prev, terms_and_conditions: def.text, tnc_template_id: def.id });
          }
        }
      } catch {}
    })();
  }, []);

  // Load source doc options when type changes.
  useEffect(() => {
    (async () => {
      try {
        let opts = [];
        if (srcType === "SO") {
          const { data } = await api.get("/sales-orders/");
          opts = (data || []).map(x => ({ id: x.id, no: x.order_no, sub: x.customer_name || "" }));
        } else if (srcType === "DC") {
          const { data } = await api.get("/delivery-challans/");
          opts = (data || []).map(x => ({ id: x.id, no: x.dc_no, sub: x.customer_name || "" }));
        } else if (srcType === "PL") {
          const { data } = await api.get("/packing-lists/");
          opts = (data || []).map(x => ({ id: x.id, no: x.pl_no, sub: x.remarks || "" }));
        } else if (srcType === "CUST") {
          // Direct-buyer flow — reuse the preloaded customers list.
          opts = customers.map(c => ({ id: c.id, no: c.code, sub: c.name }));
        }
        setSrcOptions(opts);
        // Aug 2026 · When the user selects a Sales Order, load its DC
        // picker (per-line shipped + already invoiced + balance). The
        // response drives the DC multi-select rendered below.
        if (srcType !== "SO") { setSoDcPicker(null); setPickedDcIds([]); }
      } catch (e) { setSrcOptions([]); }
    })();
  }, [srcType, customers]);

  useEffect(() => {
    if (srcType !== "SO" || !srcId) { setSoDcPicker(null); setPickedDcIds([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/gst-invoices/so-dc-picker/${srcId}`, { params: initial?.id ? { exclude_invoice_id: initial.id } : {} });
        setSoDcPicker(data || null);
        setPickedDcIds([]);
      } catch {
        setSoDcPicker(null);
      }
    })();
  }, [srcType, srcId]);

  const toggleDc = (id) => {
    // Aug 2026 · 1-to-1 rule (user ask). Single-select DC — clicking a
    // second row REPLACES the picked one instead of ticking both.
    setPickedDcIds(prev => (prev[0] === id ? [] : [id]));
  };

  const prefill = async () => {
    if (srcType === "SO") {
      if (!srcId) { toast.error("Pick a Sales Order first"); return; }
      if (!pickedDcIds.length) { toast.error("Tick at least one Delivery Challan"); return; }
      setBusy(true);
      try {
        const { data } = await api.post("/gst-invoices/prefill-from-so-dcs", { so_id: srcId, dc_ids: pickedDcIds, ...(initial?.id ? { exclude_invoice_id: initial.id } : {}) });
        setInv(prev => ({
          ...prev, ...data,
          customer_po_no: (data.references && data.references.customer_po_no) || prev.customer_po_no,
          references: data.references || prev.references,
          status: prev.status || "Draft",
          charges: prev.charges, notes: prev.notes, terms: prev.terms,
        }));
        toast.success(`Prefilled ${data.lines.length} line(s) from ${pickedDcIds.length} DC · qty locked to shipped`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Prefill failed");
      } finally { setBusy(false); }
      return;
    }
    // Direct-Buyer / legacy fallback
    if (!srcType || !srcId) { toast.error("Pick a source document first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/gst-invoices/prefill", { src_type: srcType, src_id: srcId });
      setInv(prev => ({
        ...prev, ...data,
        // Mirror customer_po_no into the top-level field so the legacy
        // column keeps working alongside the new references.pl_nos block.
        customer_po_no: (data.references && data.references.customer_po_no) || data.customer_po_no || prev.customer_po_no,
        references: data.references || prev.references,
        status: prev.status || "Draft",
        charges: prev.charges, notes: prev.notes, terms: prev.terms,
      }));
      toast.success(`Prefilled ${data.lines.length} line(s) from ${srcType} · ${data.source.no}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Prefill failed");
    } finally { setBusy(false); }
  };

  // Local totals recompute mirrors backend for a snappy edit experience.
  const totals = useMemo(() => {
    const isIntra = inv.tax_type === "intra";
    let taxable = 0, cgst = 0, sgst = 0, igst = 0, disc = 0;
    for (const ln of (inv.lines || [])) {
      const qty = Number(ln.qty) || 0;
      const rate = Number(ln.rate) || 0;
      const gross = qty * rate;
      const dA = gross * (Number(ln.discount_pct) || 0) / 100;
      const tx = gross - dA;
      taxable += tx; disc += dA;
      const gr = Number(ln.gst_rate) || 0;
      if (isIntra) { cgst += tx * gr / 200; sgst += tx * gr / 200; }
      else         { igst += tx * gr / 100; }
    }
    const freight = Number(inv.charges?.freight) || 0;
    const packing = Number(inv.charges?.packing) || 0;
    const tcsPct  = Number(inv.charges?.tcs_pct) || 0;
    const preTcs = taxable + cgst + sgst + igst + freight + packing;
    const tcsAmt = preTcs * tcsPct / 100;
    const rawGrand = preTcs + tcsAmt;
    const rounded = Math.round(rawGrand);
    const roundOff = rounded - rawGrand;
    return {
      taxable_value: taxable, total_discount: disc, total_cgst: cgst, total_sgst: sgst, total_igst: igst,
      freight, packing, tcs_amt: tcsAmt, round_off: roundOff, grand_total: rounded,
    };
  }, [inv.lines, inv.charges, inv.tax_type]);

  const patchLine = (idx, patch) => {
    setInv(prev => {
      const lines = [...(prev.lines || [])];
      lines[idx] = { ...lines[idx], ...patch };
      return { ...prev, lines };
    });
  };
  const addLine = () => setInv(prev => ({
    ...prev,
    lines: [...(prev.lines || []), { line_no: (prev.lines?.length || 0) + 1, article_no: "", description: "", hsn_code: "", unit: "MTR", qty: 0, rate: 0, discount_pct: 0, gst_rate: 5 }],
  }));
  const removeLine = (idx) => setInv(prev => ({ ...prev, lines: (prev.lines || []).filter((_, i) => i !== idx).map((l, i) => ({ ...l, line_no: i + 1 })) }));

  const saveDraft = async () => {
    setBusy(true);
    try {
      if (isNew) {
        const { data } = await api.post("/gst-invoices/", inv);
        toast.success("Draft saved");
        setInv(data);
      } else {
        const { data } = await api.put(`/gst-invoices/${initial.id}`, inv);
        toast.success("Draft updated");
        setInv(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const issue = async () => {
    // Save first if new / draft with edits.
    setBusy(true);
    try {
      let id = initial?.id;
      if (isNew) {
        const { data } = await api.post("/gst-invoices/", inv);
        id = data.id;
        setInv(data);
      } else if (initial.status === "Draft") {
        const { data } = await api.put(`/gst-invoices/${id}`, inv);
        setInv(data);
      }
      const { data: issued } = await api.post(`/gst-invoices/${id}/issue`);
      toast.success(`${issued.invoice_no} · issued`);
      onSaved && await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Issue failed");
    } finally { setBusy(false); }
  };

  const amend = async () => {
    const reason = window.prompt(`Amend ${initial.invoice_no}?\n\nEnter reason (min 4 chars, kept in audit trail):`);
    if (!reason || reason.trim().length < 4) { if (reason !== null) toast.error("Reason must be ≥ 4 chars"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/gst-invoices/${initial.id}/amend`, {
        reason: reason.trim(),
        updates: { lines: inv.lines, charges: inv.charges, buyer: inv.buyer,
                   notes: inv.notes, terms: inv.terms,
                   customer_po_no: inv.customer_po_no, vehicle_no: inv.vehicle_no, lr_no: inv.lr_no,
                   invoice_date: inv.invoice_date, references: inv.references },
      });
      toast.success(`${data.invoice_no} · amended`);
      onSaved && await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Amend failed");
    } finally { setBusy(false); }
  };

  // DP-GST-AUTH-CONTROL · Phase 2 · Clause A · Feb 2026 — explicit
  // Submit / Resubmit endpoint call from the editor footer.
  const submitFromEditor = async () => {
    const isResubmit = isRejectedGst(inv);
    if (!window.confirm(isResubmit
      ? `Resubmit ${inv.invoice_no || "this draft"} for Management Authorisation?\n\nThe invoice will re-enter the Management Authorisation Queue.`
      : `Submit ${inv.invoice_no || "this draft"} for Management Authorisation?\n\nOnce submitted, direct edits are locked until the request is Authorised or Rejected.`
    )) return;
    setBusy(true);
    try {
      // Persist first (create when new, update when existing Draft) so what
      // we submit matches what the operator sees. Phase 1B persist-then-submit.
      let id = initial?.id;
      let saved;
      if (isNew) {
        const { data } = await api.post("/gst-invoices/", inv);
        saved = data; id = data.id; setInv(data);
      } else {
        const { data } = await api.put(`/gst-invoices/${id}`, inv);
        saved = data; setInv(data);
      }
      try {
        await api.post(`/gst-invoices/${id}/submit-for-approval`);
      } catch (se) {
        toast.error("Invoice was saved as Draft, but Submit for Authorisation failed. You can submit it from the list.");
        onSaved && await onSaved();
        return;
      }
      toast.success(isResubmit
        ? `${saved.invoice_no || "Draft"} · Resubmitted for Authorisation`
        : `${saved.invoice_no || "Draft"} · Submitted for Authorisation`);
      onSaved && await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setBusy(false); }
  };

  const isIntra = inv.tax_type === "intra";
  // DP-GST-AUTH-CONTROL · Phase 2 · Clause E — Issue must be gated on
  // Management Authorisation. The UI disables the Issue button until
  // the record is Authorised so the button intent matches the backend
  // guard (which also enforces the same rule).
  const canIssue = !readOnly
    && (inv.lines?.length || 0) > 0
    && !!inv.buyer?.name
    && isAuthorisedGst(inv);
  // Phase 1B.1 · split visibility from enabled-state so operator always sees
  // the next-step Submit action on New/Draft, even when buyer/line are empty.
  const showSubmit = !readOnly && canSubmitDoc("GST")
    && (isNew || inv.status === "Draft")
    && !isAuthorisedGst(inv)
    && !isPendingGst(inv)
    && !hasEwb;
  const submitReqsMet = (inv.lines?.length || 0) > 0 && !!inv.buyer?.name;
  const enableSubmit = showSubmit && submitReqsMet;
  const submitDisabledReason = !submitReqsMet
    ? "Add buyer and at least one line to submit for authorisation"
    : "";

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent
        data-testid="gst-editor-dialog"
        className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[1200px] max-h-[92vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        description="GST Invoice action — draft, preview or cancel a domestic tax invoice."
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <FileTextIcon size={18} weight="bold" className="text-[var(--accent)]" />
            {isNew ? "New GST Invoice" : `Invoice ${inv.invoice_no || "(draft)"}`}
            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${STATUS_STYLE[inv.status] || ""}`}>
              {inv.status}
            </span>
            <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-zinc-400">
              {(inv.tax_type || "").toUpperCase()}
            </span>
          </DialogTitle>
        </DialogHeader>
        {/* Phase 1B.1 · Scoped top-right Close chip — same remediation as
            PI/EPI. Operator gets an obvious exit at the top of the long form
            without scrolling to the footer. */}
        <button
          type="button"
          data-testid="gst-close-top"
          onClick={() => !busy && onClose()}
          aria-label="Close dialog"
          className="absolute right-10 top-3 z-10 h-8 px-2.5 inline-flex items-center gap-1.5 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] font-mono uppercase tracking-widest hover:bg-white/10 disabled:opacity-40"
          disabled={busy}
        >
          <XIcon size={12} weight="bold" /> Close
        </button>

        {/* Mar 1, 2026 · Per-doc Approval Workflow (Admin/Manager only) */}
        {inv?.id && (
          <ApprovalControls
            docType="gst"
            docId={inv.id}
            docNo={inv.invoice_no || `(draft ${inv.id.slice(0, 8)})`}
            approvalStatus={inv.approval_status}
            rejectionReason={inv.rejection_reason}
            approvalHistory={inv.approval_history}
            onChange={async () => {
              try {
                const { data } = await api.get(`/gst-invoices/${inv.id}`);
                setInv(data);
                onSaved && await onSaved({ silent: true });
              } catch {}
            }}
            testIdPrefix="gst-approval"
          />
        )}

        {inv?.id && (
          <DocumentInfo docNo={inv.invoice_no} createdAt={inv.created_at} updatedAt={inv.updated_at} createdBy={inv.created_by} docLabel="Invoice No." testidPrefix="gst" />
        )}

        {/* Source picker */}
        {!readOnly && (
          <div className="grid grid-cols-12 gap-3 border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30">
            <label className="col-span-2 flex items-center text-[11px] uppercase tracking-widest text-zinc-400">Source Type</label>
            <select
              data-testid="gst-src-type"
              value={srcType}
              onChange={e => { setSrcType(e.target.value); setSrcId(""); }}
              className="col-span-2 h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm"
            >
              <option value="SO">Sales Order</option>
              <option value="CUST">Direct Buyer (no SO)</option>
            </select>
            <select
              data-testid="gst-src-id"
              value={srcId}
              onChange={e => setSrcId(e.target.value)}
              className="col-span-6 h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm"
            >
              <option value="">— pick a {srcType === "CUST" ? "customer" : srcType} —</option>
              {srcOptions.map(o => (
                <option key={o.id} value={o.id}>{o.no} {o.sub ? `· ${o.sub}` : ""}</option>
              ))}
            </select>
            <button
              data-testid="gst-prefill-btn"
              disabled={busy || !srcId || (srcType === "SO" && !pickedDcIds.length)}
              onClick={prefill}
              className="col-span-2 h-9 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40"
            >Prefill</button>
          </div>
        )}

        {/* Aug 2026 · DC multi-select (only when Source = Sales Order).
            Each row shows the DC's shipped-qty roll-up. User ticks the
            DCs being billed; qty is locked to their shipped total on
            Prefill, capped at Invoicable Balance server-side. */}
        {!readOnly && srcType === "SO" && soDcPicker && (
          <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30 space-y-2" data-testid="gst-dc-picker">
            <div className="flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-widest text-zinc-400">
                Select DC — {soDcPicker.dcs?.length || 0} available · SO {soDcPicker.so?.order_no}
              </div>
              <div className="text-[10px] font-mono text-amber-300">
                1-to-1 rule · exactly one DC per Invoice · qty locked to shipped
              </div>
            </div>
            {soDcPicker.dcs?.length === 0 ? (
              <div className="p-3 text-xs text-zinc-500">No Delivery Challans exist for this Sales Order yet.</div>
            ) : (
              <div className="max-h-56 overflow-y-auto border border-[var(--border)] rounded-sm">
                <table className="w-full text-xs">
                  <thead className="bg-[var(--bg-1)] sticky top-0">
                    <tr className="text-left text-zinc-500">
                      <th className="px-2 py-1.5 w-10"></th>
                      <th className="px-2 py-1.5">DC No</th>
                      <th className="px-2 py-1.5">Date</th>
                      <th className="px-2 py-1.5 text-right">Shipped (total)</th>
                      <th className="px-2 py-1.5">Vehicle / LR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {soDcPicker.dcs.map(dc => {
                      const checked = pickedDcIds.includes(dc.id);
                      return (
                        <tr key={dc.id} className={`border-t border-[var(--border)] cursor-pointer hover:bg-[var(--bg-1)]/60 ${checked ? "bg-[var(--accent)]/5" : ""}`} onClick={() => toggleDc(dc.id)}>
                          <td className="px-2 py-1.5"><input type="radio" name="gst-dc-pick" checked={checked} readOnly data-testid={`gst-dc-pick-${dc.dc_no}`} className="w-4 h-4 accent-[var(--accent)]" /></td>
                          <td className="px-2 py-1.5 font-mono text-[var(--accent)]">{dc.dc_no}</td>
                          <td className="px-2 py-1.5 text-zinc-400">{dc.dc_date || "—"}</td>
                          <td className="px-2 py-1.5 text-right font-mono tabular-nums">{(dc.shipped_total || 0).toFixed(3)}</td>
                          <td className="px-2 py-1.5 text-zinc-400">{[dc.vehicle_no, dc.lr_no].filter(Boolean).join(" · ") || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {soDcPicker.so_lines?.some(l => l.already_invoiced_qty > 0) && (
              <div className="text-[10px] text-amber-300 font-mono">
                Note: some SO lines already have invoiced qty — Prefill will subtract that from the balance.
              </div>
            )}
          </div>
        )}

        {/* Aug 2026 · Manual invoice-number strip. Only shown when the
            admin has switched Domestic-GST numbering to manual — the
            field is mandatory on Draft Save and validated against dup
            entries across both GST invoice collections. Read-only once
            the invoice leaves Draft. */}
        {!numCfg.auto_generation && (
          <div className="tactical-card p-3 flex items-center gap-3 border-amber-500/40 bg-amber-500/5">
            <span className="text-[10px] font-mono uppercase tracking-widest text-amber-300 px-2 py-0.5 border border-amber-500/40 rounded-sm">Manual mode</span>
            <label className="text-xs text-zinc-400 whitespace-nowrap">Invoice Number *</label>
            <input
              data-testid="gst-manual-invoice-no"
              disabled={readOnly || (inv.status && inv.status !== "Draft")}
              value={inv.invoice_no || ""}
              onChange={e => setInv(prev => ({ ...prev, invoice_no: e.target.value }))}
              placeholder="e.g. INV-2627-500"
              className="flex-1 h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono focus:outline-none focus:border-[var(--accent)]"
            />
            <span className="text-[10px] font-mono text-zinc-500">Duplicate-checked across Domestic + Export</span>
          </div>
        )}

        {/* Header */}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Invoice Date" testid="gst-date">
            <input type="date" disabled={readOnly} value={inv.invoice_date || ""} onChange={e => setInv(prev => ({ ...prev, invoice_date: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" />
          </Field>
          {/* Feb 2026 · Cust PO moved to the references strip below to
              avoid the duplicate field the user flagged. */}
          <Field label="Vehicle No"><input disabled={readOnly} value={inv.vehicle_no || ""} onChange={e => setInv(prev => ({ ...prev, vehicle_no: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
          <Field label="LR No"><input disabled={readOnly} value={inv.lr_no || ""} onChange={e => setInv(prev => ({ ...prev, lr_no: e.target.value }))} className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm" /></Field>
        </div>

        {/* Feb 2026 · Four separately-visible reference fields.
            DC / PL / SO / Cust PO auto-populate when Prefill runs and stay
            editable so a planner can attach an extra reference by hand. */}
        <div className="grid grid-cols-4 gap-3 border border-[var(--accent)]/30 rounded-sm p-3 bg-[var(--accent)]/5">
          <Field label="DC No" testid="gst-ref-dc">
            <input disabled={readOnly} value={inv.references?.dc_no || ""}
                   placeholder="—"
                   onChange={e => setInv(prev => ({ ...prev, references: { ...(prev.references || {}), dc_no: e.target.value } }))}
                   className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" />
          </Field>
          <Field label="Packing List No(s)" testid="gst-ref-pl">
            <input disabled={readOnly}
                   value={(inv.references?.pl_nos || []).join(", ")}
                   placeholder="—"
                   onChange={e => setInv(prev => ({ ...prev, references: { ...(prev.references || {}),
                     pl_nos: e.target.value.split(",").map(s => s.trim()).filter(Boolean) } }))}
                   className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" />
          </Field>
          <Field label="SO No" testid="gst-ref-so">
            <input disabled={readOnly} value={inv.references?.so_no || ""}
                   placeholder="—"
                   onChange={e => setInv(prev => ({ ...prev, references: { ...(prev.references || {}), so_no: e.target.value } }))}
                   className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" />
          </Field>
          <Field label="Customer PO No" testid="gst-ref-cust-po">
            <input disabled={readOnly} value={inv.references?.customer_po_no || ""}
                   placeholder="—"
                   onChange={e => setInv(prev => ({ ...prev,
                     customer_po_no: e.target.value,
                     references: { ...(prev.references || {}), customer_po_no: e.target.value } }))}
                   className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" />
          </Field>
        </div>

        {/* Feb 2026 · Bill To / Ship To split — mirrors the PDF layout so
            what the user sees in the editor is exactly what the customer
            gets on the printed invoice. Ship To defaults to the primary
            ship address; "Same as Bill To" one-click copies bill fields. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Party title="Seller" party={inv.seller} />
          <PartyEditable
            title="Bill To"
            testId="gst-bill-to"
            readOnly={readOnly}
            party={{
              name: inv.buyer?.name || "",
              address: inv.buyer?.billing_address || "",
              country: inv.buyer?.country || "",
              gstin: inv.buyer?.gstin || "",
              state: inv.buyer?.state || "",
              state_code: inv.buyer?.state_code || "",
              payment_terms: inv.buyer?.payment_terms,
              credit_days: inv.buyer?.credit_days,
            }}
            showPayment
            onChange={(patch) => setInv(prev => ({ ...prev, buyer: {
              ...(prev.buyer || {}),
              name: patch.name, billing_address: patch.address,
              country: patch.country, gstin: patch.gstin,
              state: patch.state,
            }}))}
          />
          <PartyEditable
            title="Ship To"
            testId="gst-ship-to"
            readOnly={readOnly}
            party={{
              name: inv.buyer?.ship_to_name || inv.buyer?.name || "",
              address: inv.buyer?.shipping_address || "",
              country: inv.buyer?.ship_country || inv.buyer?.country || "",
              gstin: inv.buyer?.shipping_gstin || "",
              state: inv.buyer?.shipping_state || "",
              state_code: inv.buyer?.shipping_state_code || "",
            }}
            hint={inv.buyer && !inv.buyer.shipping_address ? "Same as Bill To (edit to override)" : ""}
            customerId={inv.buyer?.customer_id || ""}
            onPickAddress={(a) => setInv(prev => ({ ...prev, buyer: {
              ...(prev.buyer || {}),
              ship_to_name: a.name || prev.buyer?.name || "",
              shipping_address: a.address || "",
              ship_country: a.country || prev.buyer?.country || "India",
              shipping_gstin: a.gstin || "",
              shipping_state: a.state || "",
              shipping_state_code: a.state_code || "",
              place_of_supply: a.state || (prev.buyer?.place_of_supply || ""),
            }}))}
            onCopyFromBill={() => setInv(prev => ({
              ...prev, buyer: {
                ...(prev.buyer || {}),
                ship_to_name: prev.buyer?.name || "",
                shipping_address: prev.buyer?.billing_address || "",
                ship_country: prev.buyer?.country || "",
                shipping_gstin: prev.buyer?.gstin || "",
                shipping_state: prev.buyer?.state || "",
                shipping_state_code: prev.buyer?.state_code || "",
                place_of_supply: prev.buyer?.state || "",
              },
            }))}
            onChange={(patch) => setInv(prev => ({ ...prev, buyer: {
              ...(prev.buyer || {}),
              ship_to_name: patch.name, shipping_address: patch.address,
              ship_country: patch.country, shipping_gstin: patch.gstin,
              shipping_state: patch.state,
              // Place-of-supply follows the Ship To state (India GST rule).
              place_of_supply: patch.state || (prev.buyer?.place_of_supply || ""),
            }}))}
          />
        </div>

        {/* Lines */}
        <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
          <div className="flex items-center justify-between px-3 py-2 bg-[var(--bg-2)]/40">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Line Items</div>
            {!readOnly && (
              <button data-testid="gst-add-line" onClick={addLine} className="text-xs px-2 py-1 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 rounded-sm flex items-center gap-1"><PlusIcon size={11} /> Add Line</button>
            )}
          </div>
          <table className="w-full text-xs">
            <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="px-2 py-2 text-left w-10">#</th>
                <th className="px-2 py-2 text-left">Product Code / Description</th>
                <th className="px-2 py-2 text-left w-24">Style No</th>
                <th className="px-2 py-2 text-left w-24">HSN</th>
                <th className="px-2 py-2 text-right w-20">Qty</th>
                <th className="px-2 py-2 text-left w-16">Unit</th>
                <th className="px-2 py-2 text-right w-24">Rate</th>
                <th className="px-2 py-2 text-right w-16">Disc%</th>
                <th className="px-2 py-2 text-right w-16">GST%</th>
                <th className="px-2 py-2 text-right w-24">Taxable</th>
                <th className="px-2 py-2 text-right w-24">Total</th>
                {!readOnly && <th className="w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {(inv.lines || []).length === 0 ? (
                <tr><td colSpan={12} className="px-3 py-6 text-center text-zinc-500 text-xs">No line items — use Prefill above or click Add Line.</td></tr>
              ) : inv.lines.map((ln, idx) => {
                const qty = Number(ln.qty) || 0, rate = Number(ln.rate) || 0;
                const gross = qty * rate;
                const taxable = gross * (1 - (Number(ln.discount_pct) || 0) / 100);
                const gst = taxable * (Number(ln.gst_rate) || 0) / 100;
                const total = taxable + gst;
                return (
                  <tr key={idx} className="border-t border-[var(--border)]">
                    <td className="px-2 py-1 text-zinc-500 font-mono">{idx + 1}</td>
                    <td className="px-2 py-1">
                      {/* Feb 2026 · Article picker · datalist so the user
                          can pick from the Article Master OR type a free-
                          text article number for one-off items. Picking a
                          match auto-fills description / HSN / unit / rate
                          / GST rate to keep the GST calc consistent with
                          the master. */}
                      <input
                        disabled={readOnly}
                        list={`gst-articles-master-${idx}`}
                        value={ln.article_no || ""}
                        onChange={e => {
                          const v = e.target.value;
                          const hit = articles.find(a => a.article_no === v);
                          if (hit) {
                            patchLine(idx, {
                              article_no: hit.article_no,
                              description: hit.description || ln.description,
                              hsn_code: hit.hsn_code || ln.hsn_code,
                              unit: hit.unit || ln.unit,
                              rate: hit.rate || ln.rate,
                              gst_rate: hit.gst_rate || ln.gst_rate,
                            });
                          } else {
                            patchLine(idx, { article_no: v });
                          }
                        }}
                        placeholder="Pick from master or type"
                        className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono"
                      />
                      <datalist id={`gst-articles-master-${idx}`}>
                        {articles.map(a => (
                          <option key={a.id} value={a.article_no}>
                            {a.description || ""} {a.hsn_code ? `· HSN ${a.hsn_code}` : ""}
                          </option>
                        ))}
                      </datalist>
                      <input disabled={readOnly} value={ln.description || ""} onChange={e => patchLine(idx, { description: e.target.value })} placeholder="Description (auto-filled or manual)" className="w-full h-7 px-1.5 mt-1 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-[10px] text-zinc-400" />
                    </td>
                    <td className="px-2 py-1 font-mono text-xs text-zinc-200 whitespace-nowrap" data-testid={`gst-line-style-${idx}`}>{ln.style_no || savedStyles[idx] || "—"}</td>
                    <td className="px-2 py-1"><input disabled={readOnly} data-testid={`gst-line-hsn-${idx}`} value={ln.hsn_code || ""} onChange={e => patchLine(idx, { hsn_code: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono" /></td>
                    <td className="px-2 py-1">
                      <input type="number" step="0.01" disabled={readOnly || !!ln.locked_from_dc} data-testid={`gst-line-qty-${idx}`} value={ln.qty ?? 0} onChange={e => patchLine(idx, { qty: e.target.value })} className={`w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums ${ln.locked_from_dc ? "opacity-70" : ""}`} />
                      {ln.locked_from_dc && (
                        <div className="text-[9px] font-mono text-zinc-500 mt-0.5 text-right whitespace-nowrap" data-testid={`gst-line-balance-${idx}`}>
                          Shipped {Number(ln.shipped_qty || 0).toFixed(2)} · Bal {Number(ln.invoicable_balance || 0).toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1"><input disabled={readOnly} value={ln.unit || ""} onChange={e => patchLine(idx, { unit: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" disabled={readOnly} data-testid={`gst-line-rate-${idx}`} value={ln.rate ?? 0} onChange={e => patchLine(idx, { rate: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" disabled={readOnly} value={ln.discount_pct ?? 0} onChange={e => patchLine(idx, { discount_pct: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" /></td>
                    <td className="px-2 py-1"><input type="number" step="0.01" disabled={readOnly} data-testid={`gst-line-gst-${idx}`} value={ln.gst_rate ?? 0} onChange={e => patchLine(idx, { gst_rate: e.target.value })} className="w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" /></td>
                    <td className="px-2 py-1 text-right font-mono tabular-nums text-zinc-300">{fmt(taxable)}</td>
                    <td className="px-2 py-1 text-right font-mono tabular-nums font-semibold">{fmt(total)}</td>
                    {!readOnly && (
                      <td className="px-2 py-1 text-right"><button title="Remove" onClick={() => removeLine(idx)} className="text-red-400 hover:text-red-300"><TrashIcon size={12} /></button></td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Charges + Totals */}
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-[var(--border)] rounded-sm p-3 space-y-2">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Extra Charges</div>
            <NumRow label="Freight"    disabled={readOnly} value={inv.charges?.freight} onChange={v => setInv(prev => ({ ...prev, charges: { ...prev.charges, freight: v } }))} testid="gst-charge-freight" />
            <NumRow label="Packing"    disabled={readOnly} value={inv.charges?.packing} onChange={v => setInv(prev => ({ ...prev, charges: { ...prev.charges, packing: v } }))} testid="gst-charge-packing" />
            <NumRow label="TCS %"      disabled={readOnly} value={inv.charges?.tcs_pct} onChange={v => setInv(prev => ({ ...prev, charges: { ...prev.charges, tcs_pct: v } }))} testid="gst-charge-tcs" />
            <div className="pt-2 border-t border-[var(--border)]">
              <label className="text-[11px] uppercase tracking-widest text-zinc-500">Notes</label>
              <textarea rows={2} disabled={readOnly} value={inv.notes || ""} onChange={e => setInv(prev => ({ ...prev, notes: e.target.value }))} className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" />
            </div>
            {/* Feb 2026 · Terms & Conditions template picker + editor. */}
            <div className="pt-2 border-t border-[var(--border)]">
              <div className="flex items-center gap-2 mb-1">
                <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex-1">Terms &amp; Conditions</label>
                <select
                  data-testid="gst-tnc-template"
                  disabled={readOnly}
                  value={inv.tnc_template_id || ""}
                  onChange={e => {
                    const id = e.target.value;
                    const tpl = tncTemplates.find(t => t.id === id);
                    setInv(prev => ({ ...prev,
                      tnc_template_id: id,
                      terms_and_conditions: tpl ? tpl.text : (prev.terms_and_conditions || ""),
                    }));
                  }}
                  className="h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-[11px]"
                >
                  <option value="">— Select template —</option>
                  {tncTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <textarea rows={4} disabled={readOnly} data-testid="gst-tnc-text"
                value={inv.terms_and_conditions || ""}
                onChange={e => setInv(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
                placeholder="Pick a template or type your own T&C — printed above the bank details on the PDF"
                className="w-full mt-1 px-2 py-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" />
            </div>
          </div>
          <div className="border border-[var(--border)] rounded-sm p-3 space-y-1.5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1">Totals</div>
            <TotRow label="Taxable"     value={totals.taxable_value} />
            {isIntra ? <><TotRow label="CGST" value={totals.total_cgst} /><TotRow label="SGST" value={totals.total_sgst} /></>
                     : <TotRow label="IGST" value={totals.total_igst} />}
            <TotRow label="Freight"     value={totals.freight} />
            <TotRow label="Packing"     value={totals.packing} />
            <TotRow label="TCS"         value={totals.tcs_amt} />
            <TotRow label="Round Off"   value={totals.round_off} />
            <div className="border-t border-[var(--border)] pt-1.5 flex items-center justify-between font-mono">
              <span className="text-[11px] uppercase tracking-widest text-zinc-300">Grand Total</span>
              <span className="text-lg font-bold text-[var(--accent)]" data-testid="gst-grand-total">₹ {fmt(totals.grand_total)}</span>
            </div>
          </div>
        </div>

        {/* Amendment history */}
        {(inv.amendments || []).length > 0 && (
          <div className="border border-amber-500/30 rounded-sm p-3 bg-amber-500/5">
            <div className="text-[11px] font-mono uppercase tracking-widest text-amber-300 mb-2 flex items-center gap-2"><WarningIcon size={12} weight="bold" /> Amendment history</div>
            {inv.amendments.map((a, i) => (
              <div key={i} className="text-[11px] text-zinc-300 border-b border-amber-500/10 last:border-0 py-1">
                <span className="text-amber-300 font-mono">{a.at?.slice(0, 19).replace("T", " ")}</span>
                {" · "}<span className="text-zinc-400">{a.by}</span>{" · "}{a.reason}
              </div>
            ))}
          </div>
        )}

        {inv.status === "Cancelled" && inv.cancelled && (
          <div className="border border-red-500/40 rounded-sm p-3 bg-red-500/5 text-[11px] text-red-200">
            <b>Cancelled</b> · {inv.cancelled.at?.slice(0, 19).replace("T", " ")} by {inv.cancelled.by} · {inv.cancelled.reason}
          </div>
        )}

        <DialogFooter className="gap-2">
          <button data-testid="gst-cancel-editor" onClick={onClose} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm">Close</button>
          {initial?.id && <DocDownloadButtons type="GST" id={initial.id} testPrefix="gst-view" />}
          {/* DP-GST-AUTH-CONTROL · Phase 2 · Clause G · state banners so
              the operator knows why direct edits are suppressed. */}
          {initial && isPendingGst(initial) && (
            <span data-testid="gst-locked-pending-auth" className="inline-flex items-center px-2 py-1 rounded-sm border border-[var(--accent)]/50 text-[var(--accent)] bg-[var(--accent)]/10 text-[10px] font-mono uppercase tracking-widest">Pending Management Authorisation</span>
          )}
          {initial && isAuthorisedGst(initial) && initial.status === "Draft" && (
            <span data-testid="gst-locked-authorised" className="inline-flex items-center px-2 py-1 rounded-sm border border-emerald-500/50 text-emerald-300 bg-emerald-500/10 text-[10px] font-mono uppercase tracking-widest">Authorised · direct edit locked</span>
          )}
          {!readOnly && !isAmendable && (
            <>
              <button data-testid="gst-save-draft" disabled={busy} onClick={saveDraft} className="h-9 px-4 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40">Save as Draft</button>
              {/* Explicit Submit / Resubmit for Management Authorisation.
                  Phase 1B.1 — ALWAYS rendered in showSubmit lifecycle states
                  (Not-Submitted / Draft / Rejected), DISABLED until buyer+line
                  are present. Save ≠ Submit — the record only enters the
                  Management Authorisation Queue on this action. */}
              {showSubmit && (
                <button
                  data-testid={isRejectedGst(inv) ? "gst-resubmit" : "gst-submit"}
                  disabled={busy || !enableSubmit}
                  onClick={submitFromEditor}
                  title={!enableSubmit ? submitDisabledReason : ""}
                  className={`h-9 px-4 text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5 ${
                    isRejectedGst(inv)
                      ? "bg-amber-500 hover:bg-amber-600 text-white"
                      : "border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10"
                  }`}
                >
                  {isRejectedGst(inv) ? "Resubmit for Authorisation" : "Submit for Authorisation"}
                </button>
              )}
              {isAuthorisedGst(inv) && (
                <button data-testid="gst-issue" disabled={busy || !canIssue} title={!canIssue && !isAuthorisedGst(inv) ? "Requires Management Authorisation before Issue" : ""} onClick={issue} className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"><CheckCircleIcon size={12} weight="bold" /> Issue Invoice</button>
              )}
            </>
          )}
          {isAmendable && (
            <button data-testid="gst-amend-save" disabled={busy} onClick={amend} className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"><PencilLineIcon size={12} weight="bold" /> Save Changes (Amend)</button>
          )}
          {initial && ["Issued", "Amended"].includes(initial.status) && hasEwb && (
            <span data-testid="gst-locked-by-ewb" className="inline-flex items-center px-2 py-1 rounded-sm border border-red-500/50 text-red-300 bg-red-500/10 text-[10px] font-mono uppercase tracking-widest">Locked · e-Way Bill Issued</span>
          )}
          {initial?.invoice_no && (
            <button data-testid="gst-editor-print" disabled={busy} onClick={async () => {
              // Mar 2026 · Editor Print now uses the A4 HTML flow — the
              // Print/PDF (ReportLab) button was removed to keep a single
              // canonical output format across the grid + editor.
              const res = await api.get(`/gst-invoices/${initial.id}/print.html`, { responseType: "text" });
              const blob = new Blob([res.data], { type: "text/html;charset=utf-8" });
              const url  = URL.createObjectURL(blob);
              window.open(url, "_blank");
              setTimeout(() => URL.revokeObjectURL(url), 60000);
            }} className="h-9 px-4 border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs font-mono uppercase tracking-widest rounded-sm disabled:opacity-40 flex items-center gap-1.5"><PrinterIcon size={12} weight="bold" /> Browser Print</button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">{label}</label>
      {children}
    </div>
  );
}

function Party({ title, party }) {
  const isBuyer = title === "Buyer";
  return (
    <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30">
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-1">{title}</div>
      <div className="text-sm text-white font-semibold">{party?.name || "—"}</div>
      <div className="text-[11px] text-zinc-400 whitespace-pre-line">{party?.address || party?.billing_address || "—"}</div>
      <div className="text-[11px] text-zinc-300 mt-1">
        <span className="text-zinc-500">GSTIN:</span> {party?.gstin || "—"}
        {party?.state ? <> · <span className="text-zinc-500">State:</span> {party.state} · {party.state_code || "—"}</> : null}
      </div>
      {/* Feb 2026 · Payment Terms + Credit Days flow onto every invoice from
          the buyer's Customer Master so the customer copy shows them without
          re-entry. Rendered only on the Buyer panel. */}
      {isBuyer && (party?.payment_terms || party?.credit_days) && (
        <div className="text-[11px] mt-2 flex items-center gap-2 flex-wrap" data-testid="gst-buyer-payment-terms">
          <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-300 bg-emerald-500/5 font-mono">
            {party?.payment_terms || "—"}
          </span>
          {party?.credit_days ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/5 font-mono">
              Credit {party.credit_days} days
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}

// Feb 2026 · Editable Bill To / Ship To card for the GST Invoice editor.
// Read-only mode preserves the historical look-and-feel; edit mode swaps
// in tight inputs so users can override per-invoice without leaving the
// dialog. GSTIN is upper-cased on entry; place-of-supply is derived from
// the Ship To state in the parent callback (India GST rule).
function PartyEditable({ title, party, readOnly, testId, hint, onChange, onCopyFromBill, showPayment, customerId, onPickAddress }) {
  const inputCls = "w-full h-7 px-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs";
  const areaCls  = "w-full min-h-[48px] px-1.5 py-1 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs resize-y";
  const [addrs, setAddrs] = useState([]);
  useEffect(() => {
    // Feb 28, 2026 · Ship-To gets an address picker when the parent
    // supplies a `customerId` + `onPickAddress`. Loads once per change.
    if (!customerId || !onPickAddress) { setAddrs([]); return; }
    api.get(`/gst-invoices/customer-addresses/${customerId}`)
      .then(({ data }) => setAddrs(data?.addresses || []))
      .catch(() => setAddrs([]));
  }, [customerId, onPickAddress]);
  return (
    <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30 space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">{title}</div>
        {!readOnly && onCopyFromBill && (
          <button
            type="button"
            onClick={onCopyFromBill}
            data-testid={`${testId}-copy-from-bill`}
            className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline"
          >
            Same as Bill To
          </button>
        )}
      </div>
      {!readOnly && onPickAddress && addrs.length > 0 && (
        <div className="flex items-center gap-2" data-testid={`${testId}-picker-wrap`}>
          <select
            data-testid={`${testId}-picker`}
            onChange={e => { const a = addrs.find(x => x.id === e.target.value); if (a) onPickAddress(a); e.target.value = ""; }}
            defaultValue=""
            className="flex-1 h-8 px-2 bg-[var(--bg-0)] border border-[var(--accent)]/60 hover:border-[var(--accent)] rounded-sm text-xs text-[var(--accent)] font-mono uppercase tracking-wide cursor-pointer"
          >
            <option value="" disabled>▸ Pick another address from Customer Master…</option>
            {addrs.map(a => (
              <option key={a.id} value={a.id}>
                {a.label}
                {a.is_primary_ship ? " · PRIMARY SHIP" : a.is_primary_bill ? " · PRIMARY BILL" : ""}
                {a.state ? ` — ${a.state}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}
      {readOnly ? (
        <>
          <div className="text-sm text-white font-semibold">{party?.name || "—"}</div>
          <div className="text-[11px] text-zinc-400 whitespace-pre-line">{party?.address || "—"}</div>
          <div className="text-[11px] text-zinc-300">
            <span className="text-zinc-500">GSTIN:</span> {party?.gstin || "—"}
            {party?.state ? <> · <span className="text-zinc-500">State:</span> {party.state} · {party.state_code || "—"}</> : null}
          </div>
        </>
      ) : (
        <>
          <input
            value={party?.name || ""}
            onChange={e => onChange({ ...party, name: e.target.value })}
            placeholder="Party name"
            className={`${inputCls} font-semibold text-white`}
            data-testid={`${testId}-name`}
          />
          <textarea
            value={party?.address || ""}
            onChange={e => onChange({ ...party, address: e.target.value })}
            placeholder="Address (multi-line)"
            className={areaCls}
            data-testid={`${testId}-address`}
          />
          <div className="grid grid-cols-2 gap-1.5">
            <input
              value={party?.state || ""}
              onChange={e => onChange({ ...party, state: e.target.value })}
              placeholder="State"
              className={inputCls}
              data-testid={`${testId}-state`}
            />
            <input
              value={party?.gstin || ""}
              onChange={e => onChange({ ...party, gstin: e.target.value.toUpperCase() })}
              placeholder="GSTIN"
              className={`${inputCls} font-mono uppercase`}
              data-testid={`${testId}-gstin`}
            />
          </div>
          <input
            value={party?.country || ""}
            onChange={e => onChange({ ...party, country: e.target.value })}
            placeholder="Country"
            className={inputCls}
            data-testid={`${testId}-country`}
          />
        </>
      )}
      {hint && <div className="text-[10px] text-zinc-500 italic mt-1">{hint}</div>}
      {showPayment && (party?.payment_terms || party?.credit_days) && (
        <div className="text-[11px] mt-2 flex items-center gap-2 flex-wrap" data-testid="gst-buyer-payment-terms">
          <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-300 bg-emerald-500/5 font-mono">{party?.payment_terms || "—"}</span>
          {party?.credit_days ? <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/5 font-mono">Credit {party.credit_days} days</span> : null}
        </div>
      )}
    </div>
  );
}

function NumRow({ label, value, onChange, disabled, testid }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <label className="text-[11px] uppercase tracking-widest text-zinc-500 flex-1">{label}</label>
      <input type="number" step="0.01" data-testid={testid} disabled={disabled} value={value ?? 0} onChange={e => onChange(e.target.value)} className="w-32 h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs text-right tabular-nums" />
    </div>
  );
}

function TotRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-[11px] font-mono">
      <span className="text-zinc-500 uppercase tracking-widest">{label}</span>
      <span className="tabular-nums text-zinc-200">{fmt(value)}</span>
    </div>
  );
}
